<template>
  <!-- Inline: render the panel directly -->
  <SCACalendarPanel
    v-if="inline"
    v-model="model"
    v-model:all-day="allDayModel"
    v-model:months="monthsModel"
    v-model:columns="columnsModel"
    :mode="mode"
    :readonly="readonly || disabled"
    :config="config"
    inline
    :class="{ [$style.disabledPanel]: disabled }"
  />
  <!-- Dropdown: renders the panel inside a popup trigger -->
  <SCADropdown
    v-else
    v-model:is-open="isOpen"
    :class="$style.dropdown"
    placement="bottom-start"
    padding-upset
    no-mobile-margin
    :offset-x="offsetX"
    :offset-y="5"
    @focusout="closeOnFocusLeave"
  >
    <template #trigger="{ target }">
      <!-- Manual entry: masked section editor(s); the calendar opens only via `isOpen` (e.g. the field's label icon) -->
      <SCAInputBase
        v-if="manualInput"
        underline-override
        :appearance="appearance"
        :focused="isOpen || manualFocused"
        :alert="alert || inputInvalid"
      >
        <div
          ref="offsetScope"
          :class="$style.manualTrigger"
          @focusin="manualFocused = true"
          @focusout="manualFocused = false"
          @keydown.enter="closeOnEnter"
        >
          <!-- Invisible input so SCAInputBase applies its underline/disabled styling (it keys off :has(input)) -->
          <input readonly inert :class="$style.styleProbe" />
          <div :class="[$style.manualContent, { [$style.withClear]: showClear }]">
            <!-- Range: two section editors presented as one input -->
            <div v-if="isRange" :class="$style.manualRangeGroup">
              <SCADatetimeInputInline
                ref="startEditor"
                v-model="rangeStartManual"
                v-model:invalid="startInvalid"
                :format="itemFormat"
                :disabled="disabled"
                :readonly="readonly"
                test-id="datetime-manual-start"
                @next="endEditor?.focus()"
              />
              <span :class="$style.manualRangeSeparator">–</span>
              <SCADatetimeInputInline
                ref="endEditor"
                v-model="rangeEndManual"
                v-model:invalid="endInvalid"
                :format="itemFormat"
                :disabled="disabled"
                :readonly="readonly"
                test-id="datetime-manual-end"
                @prev="startEditor?.focusLast()"
              />
            </div>
            <SCADatetimeInputInline
              v-else
              ref="singleEditor"
              v-model="singleManual"
              v-model:invalid="singleInvalid"
              :format="itemFormat"
              :disabled="disabled"
              :readonly="readonly"
              test-id="datetime-manual"
            />
            <!-- Clear icon -->
            <button v-if="showClear" type="button" tabindex="-1" :class="$style.clearButton" @click.stop="clear">
              <SCASvgIcon icon="phosphor/x" size="12px" :class="$style.clearIcon" />
            </button>
            <!-- Custom content -->
            <slot />
          </div>
        </div>
      </SCAInputBase>
      <SCAInputBase v-else underline-override :appearance="appearance" :focused="isOpen" :alert="alert">
        <div ref="offsetScope" :class="[$style.dropdownTrigger, { [$style.disabled]: disabled }]">
          <input readonly :aria-haspopup="true" :aria-expanded="isOpen" @keydown.enter.prevent="isOpen = true" />
          <button
            :id="htmlFor"
            type="button"
            tabindex="-1"
            :popovertarget="target"
            :disabled="disabled"
            :aria-label="placeholder"
            :class="$style.triggerButton"
          />
          <div :class="[$style.content, { [$style.withClear]: showClear }]">
            <template v-if="hasValue">
              <!-- Range value: each side is its own flex item so it wraps; the group keeps a tight gap while the parent .content spaces the slot apart -->
              <div v-if="isRange" :class="$style.rangeGroup">
                <span :class="rangeStart ? $style.value : $style.placeholder">{{ rangeStart || itemFormat }}</span>
                <span :class="$style.rangeSeparator">–</span>
                <span :class="rangeEnd ? $style.value : $style.placeholder">{{ rangeEnd || itemFormat }}</span>
              </div>
              <!-- Selected single date value -->
              <span v-else :class="$style.value">{{ displayValue }}</span>
              <!-- Clear icon -->
              <button v-if="showClear" type="button" tabindex="-1" :class="$style.clearButton" @click.stop="clear">
                <SCASvgIcon icon="phosphor/x" size="12px" :class="$style.clearIcon" />
              </button>
            </template>
            <template v-else>
              <!-- Placeholder -->
              <span :class="$style.placeholder">{{ placeholder }}</span>
            </template>
            <!-- Custom content -->
            <slot />
          </div>
        </div>
      </SCAInputBase>
    </template>

    <SCACalendarPanel
      v-model="model"
      v-model:all-day="allDayModel"
      v-model:months="monthsModel"
      v-model:columns="columnsModel"
      :mode="mode"
      :readonly="readonly || disabled"
      :config="config"
      @selection-complete="onSelectionComplete"
      @keydown.enter="closeOnEnter($event, true)"
    />
  </SCADropdown>
</template>

<script setup lang="ts">
import { applyDatePrecision, applyTimePrecision, resolveDateFnsLocale } from "@scayla/core";
import { format } from "date-fns";
import { computed, onMounted, ref, watch, watchEffect } from "vue";
import { useI18n } from "vue-i18n";

import SCADropdown from "../Dropdown/SCADropdown.vue";
import SCAInputBase from "../SCAInputBase.vue";
import SCASvgIcon from "../SCASvgIcon.vue";
import type { DatetimePickerProps, DatetimePickerValue, RangeValue, SingleValue } from "./datetimePickerTypes";
import { hasTime, isRangeMode } from "./datetimePickerTypes";
import SCACalendarPanel from "./SCACalendarPanel.vue";
import SCADatetimeInputInline from "./SCADatetimeInputInline.vue";

const props = withDefaults(defineProps<DatetimePickerProps>(), {
  mode: "date",
  inline: false,
  readonly: false,
  appearance: undefined,
  config: undefined,
  dateFormat: "dd.MM.yyyy",
  timeFormat: "HH:mm",
  datePrecision: undefined,
  timePrecision: undefined,
  clearable: true,
  placeholder: undefined,
  disabled: false,
  htmlFor: undefined,
  alert: false,
  manualInput: false,
});

const { locale } = useI18n();
const dateFnsLocale = computed(() => resolveDateFnsLocale(locale.value));

const model = defineModel<DatetimePickerValue>({ default: null });
const allDayModel = defineModel<boolean>("allDay", { default: false });

const monthsModel = defineModel<number>("months", { default: 1 });
const columnsModel = defineModel<number>("columns", { default: 1 });

const isOpen = defineModel<boolean>("isOpen", { default: false });

/** True while a typed entry is incomplete or impossible (manual-input mode only). */
const inputInvalid = defineModel<boolean>("invalid", { default: false });

const isRange = computed(() => isRangeMode(props.mode));

const DEFAULT_OFFSET_X = -4;
const LABEL_FIELD_PADDING = 16;

const offsetScope = ref<HTMLElement>();
const labelSize = ref<number | null>(null);

function readLabelSize() {
  if (!offsetScope.value) return;
  const raw = getComputedStyle(offsetScope.value).getPropertyValue("--sca-label-size").trim();
  const parsed = Number.parseFloat(raw);
  labelSize.value = Number.isFinite(parsed) ? parsed : null;
}

const offsetX = computed(() => (labelSize.value != null ? -(labelSize.value + LABEL_FIELD_PADDING) : DEFAULT_OFFSET_X));

onMounted(readLabelSize);

watch(isOpen, (open) => open && readLabelSize());

// --- Manual (masked) entry -------------------------------------------------

const manualFocused = ref(false);

// Range editors, so completing the start date can jump focus to the end date (and back).
const singleEditor = ref<InstanceType<typeof SCADatetimeInputInline>>();
const startEditor = ref<InstanceType<typeof SCADatetimeInputInline>>();
const endEditor = ref<InstanceType<typeof SCADatetimeInputInline>>();

/** Focus the masked editor's first section (range → start editor). Manual-input mode only. */
function focus() {
  if (!props.manualInput) return;
  (isRange.value ? startEditor.value : singleEditor.value)?.focus();
}
defineExpose({ focus });

const singleInvalid = ref(false);
const startInvalid = ref(false);
const endInvalid = ref(false);
watchEffect(() => {
  inputInvalid.value = props.manualInput && (singleInvalid.value || startInvalid.value || endInvalid.value);
});

const singleManual = computed<Date | null>({
  get: () => (isRange.value ? null : ((model.value as SingleValue) ?? null)),
  set: (value) => {
    model.value = value;
  },
});

const rangeStartManual = computed<Date | null>({
  get: () => ((model.value as RangeValue | null) ?? [null, null])[0] ?? null,
  set: (value) => {
    const current = (model.value as RangeValue | null) ?? [null, null];
    model.value = [value, current[1]];
  },
});

const rangeEndManual = computed<Date | null>({
  get: () => ((model.value as RangeValue | null) ?? [null, null])[1] ?? null,
  set: (value) => {
    const current = (model.value as RangeValue | null) ?? [null, null];
    model.value = [current[0], value];
  },
});

// ---------------------------------------------------------------------------

const displayDateFormat = computed(() => applyDatePrecision(props.dateFormat, props.datePrecision));
const displayTimeFormat = computed(() => applyTimePrecision(props.timeFormat, props.timePrecision));

const itemFormat = computed(() => {
  if (props.mode === "time" || props.mode === "timeRange") return displayTimeFormat.value;
  if (hasTime(props.mode)) return `${displayDateFormat.value} ${displayTimeFormat.value}`;
  return displayDateFormat.value;
});

function formatOne(d: Date | null): string {
  return d ? format(d, itemFormat.value, { locale: dateFnsLocale.value }) : "";
}

const hasValue = computed(() => {
  if (isRange.value) {
    const [a, b] = (model.value as RangeValue | null) ?? [null, null];
    return !!a || !!b;
  }
  return !!model.value;
});

const showClear = computed(() => props.clearable && hasValue.value && !props.disabled);

const rangeStart = computed(() => formatOne(((model.value as RangeValue | null) ?? [null, null])[0]));
const rangeEnd = computed(() => formatOne(((model.value as RangeValue | null) ?? [null, null])[1]));

const displayValue = computed(() => {
  if (isRange.value) {
    if (!rangeStart.value && !rangeEnd.value) return "";
    return `${rangeStart.value} – ${rangeEnd.value}`;
  }
  return formatOne(model.value as Date | null);
});

function onSelectionComplete() {
  isOpen.value = false;
}

function closeOnEnter(event: KeyboardEvent, fromPanel = false) {
  if (event.defaultPrevented || !isOpen.value) return;
  if (!(event.target as Element | null)?.closest('[role="spinbutton"]')) return;
  if (singleInvalid.value || startInvalid.value || endInvalid.value) return;
  event.preventDefault();
  isOpen.value = false;
  if (fromPanel) focus();
}

function closeOnFocusLeave(event: FocusEvent) {
  const next = event.relatedTarget;
  if (!isOpen.value || !(next instanceof HTMLElement) || next.getAttribute("tabindex") === "-1") return;
  if ((event.currentTarget as HTMLElement).contains(next)) return;
  isOpen.value = false;
}

function clear() {
  model.value = isRange.value ? [null, null] : null;
}
</script>

<style module lang="scss">
@use "../../styles/generated/figma_typo_mixins.scss" as FigmaTypoMixins;

.disabledPanel {
  opacity: 0.6;
  pointer-events: none;
}

.dropdown {
  width: 100%;
}

.manualTrigger {
  position: relative;
  width: 100%;

  .styleProbe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
    border: none;
    background: transparent;
    opacity: 0;
    pointer-events: none;
  }

  .manualContent {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: var(--sca-input-content-align, flex-start);
    width: 100%;
    min-height: 100%;
    column-gap: var(--space-component-padding-large);

    &.withClear {
      padding-right: var(--space-component-padding-large);
    }
  }

  .manualRangeGroup {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    min-width: 0;
    column-gap: var(--space-component-padding-xSmall);
  }

  .manualRangeSeparator {
    @include FigmaTypoMixins.typo-body-medium;
    color: var(--color-content-primary);
  }
}

.dropdownTrigger {
  position: relative;
  width: 100%;
  cursor: pointer;

  &.disabled {
    cursor: auto;
    opacity: 0.5;
  }

  > input,
  .triggerButton {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;

    &:disabled {
      cursor: auto;
    }
  }

  .content {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: var(--sca-input-content-align, flex-start);
    width: 100%;
    min-height: 100%;
    column-gap: var(--space-component-padding-large);
    pointer-events: none;

    &.withClear {
      padding-right: var(--space-component-padding-large);
    }

    .rangeGroup {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      min-width: 0;
      column-gap: var(--space-component-padding-xSmall);
    }

    .value,
    .placeholder {
      overflow: hidden;
      overflow-wrap: normal;
      word-break: keep-all;
    }

    .value {
      @include FigmaTypoMixins.typo-body-medium;
      color: var(--color-content-primary);
    }

    .placeholder {
      @include FigmaTypoMixins.typo-body-medium;
      color: var(--color-content-tertiary);
    }

    .rangeSeparator {
      @include FigmaTypoMixins.typo-body-medium;
      color: var(--color-content-primary);
    }
  }
}

.clearButton {
  position: absolute;
  top: 50%;
  right: 0;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  transform: translateY(-50%);
  color: var(--color-content-tertiary);
  cursor: pointer;
  pointer-events: auto;

  opacity: 0;
  transition: opacity 0.1s ease;

  .manualTrigger:hover &,
  .manualTrigger:focus-within &,
  .dropdownTrigger:hover &,
  .dropdownTrigger:focus-within & {
    opacity: 1;
  }

  &:hover {
    color: var(--color-content-primary);
  }
}

.clearIcon {
  pointer-events: none;
}
</style>
